// The Import files screen's state transitions, kept pure next to importScreenState.ts so
// the dialog only sequences them. Every transition ends in `normalised`, which keeps each
// file's choice valid and opens a lone file. Ported from Android's `ImportScreenUpdates.kt`.

import type { ImportFileResult } from './importReader';
import { emptyPreview } from './importRules';
import type { ImportPreview } from './importTypes';
import type { DayMatch } from './importSnap';
import {
  findEntry,
  importFileEntry,
  initialChoice,
  options,
  pendingEntry,
  type ImportChoice,
  type ImportFileEntry,
  type ImportFileSettings,
  type ImportScreenState,
  type PeakCsvCheck,
  lookBar,
  snapAllEntries
} from './importScreenState';

/**
 * A choice that's no longer offered falls back to the file's initial choice; and a file that
 * has just become the only one on the screen opens.
 */
export function normalised(s: ImportScreenState, previousCount: number): ImportScreenState {
  const fixed = s.entries.map(e => {
    if (!e.preview || options(s, e).includes(e.settings.choice)) return e;
    const fallback = options(s, e).includes(initialChoice(e)) ? initialChoice(e) : 'skip';
    return { ...e, settings: { ...e.settings, choice: fallback } };
  });
  const open =
    fixed.length === 1 && previousCount !== 1
      ? fixed[0].id
      : s.openId && fixed.some(e => e.id === s.openId)
        ? s.openId
        : null;
  return { ...s, entries: fixed, openId: open };
}

/**
 * Queues files that are still being read. A file already on the screen isn't added twice — a
 * ZIP archive counts as on the screen while any of the files it was unpacked into is.
 */
export function withAdded(s: ImportScreenState, files: Array<{ id: string; name: string }>): ImportScreenState {
  const known = new Set(s.entries.flatMap(e => [e.id, e.source]));
  const seen = new Set<string>();
  const added = files.filter(f => {
    if (known.has(f.id) || seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  });
  if (!added.length) return s;
  return normalised({ ...s, entries: [...s.entries, ...added.map(f => pendingEntry(f.id, f.name))] }, s.entries.length);
}

/** A file has been read: its defaults come from the start screen. */
export function withPreview(s: ImportScreenState, id: string, preview: ImportPreview): ImportScreenState {
  const current = findEntry(s, id);
  if (!current) return s;
  const read = importFileEntry(current.id, current.name, preview, s.intent, current.source);
  return normalised({ ...s, entries: s.entries.map(e => (e.id === id ? read : e)) }, s.entries.length);
}

/** A stable id for the route file at `index` of the ZIP archive whose id is `archiveId`. */
export const archiveEntryId = (archiveId: string, index: number, name: string): string => `${archiveId}#${index}/${name}`;

/**
 * A file has been read into `files` (`readImportFiles`). One file is `withPreview`. A ZIP
 * archive's card becomes one card per route file inside it, in the archive's order and where
 * the ZIP's card was, each with its own id and defaults.
 */
export function withFiles(s: ImportScreenState, id: string, files: ImportFileResult[]): ImportScreenState {
  if (!files.length) return withPreview(s, id, emptyPreview(null, 'unsupported_format'));
  if (files.length === 1 && !files[0].fromArchive) return withPreview(s, id, files[0].preview);
  const current = findEntry(s, id);
  if (!current) return s;
  const unpacked = files.map((f, i) => importFileEntry(archiveEntryId(id, i, f.name), f.name, f.preview, s.intent, current.source));
  const at = s.entries.indexOf(current);
  const next = [...s.entries.slice(0, at), ...unpacked, ...s.entries.slice(at + 1)];
  // A ZIP that was the one open card, and holds one route file, opens that file instead.
  const open = s.openId === id ? (unpacked.length === 1 && next.length === 1 ? unpacked[0].id : null) : s.openId;
  return normalised({ ...s, entries: next, openId: open }, s.entries.length);
}

/** A bagged-peaks CSV's dry run is back from the server. */
export function withPeakCheck(s: ImportScreenState, id: string, check: PeakCsvCheck): ImportScreenState {
  if (!findEntry(s, id)) return s;
  return normalised({ ...s, entries: s.entries.map(e => (e.id === id ? { ...e, peakCheck: check } : e)) }, s.entries.length);
}

export function withRemoved(s: ImportScreenState, id: string): ImportScreenState {
  if (!findEntry(s, id)) return s;
  return normalised(
    { ...s, entries: s.entries.filter(e => e.id !== id), openId: s.openId === id ? null : s.openId },
    s.entries.length
  );
}

/** Expands a file, or collapses it when it's the one already open. */
export const withToggledOpen = (s: ImportScreenState, id: string): ImportScreenState => ({ ...s, openId: s.openId === id ? null : id });

export const withLookToggled = (s: ImportScreenState): ImportScreenState => ({ ...s, lookExpanded: !(lookBar(s)?.open ?? s.lookExpanded) });

function updateSettings(
  s: ImportScreenState,
  id: string,
  change: (e: ImportFileEntry) => ImportFileSettings
): ImportScreenState {
  return { ...s, entries: s.entries.map(e => (e.id === id && e.preview ? { ...e, settings: change(e) } : e)) };
}

/** Picks a file's "Import as"; a choice the file doesn't offer is ignored. */
export function withChoice(s: ImportScreenState, id: string, choice: ImportChoice): ImportScreenState {
  const target = findEntry(s, id);
  if (!target?.preview || !options(s, target).includes(choice)) return s;
  return updateSettings(s, id, e => ({ ...e.settings, choice }));
}

export const withJoinDays = (s: ImportScreenState, id: string, joinDays: boolean): ImportScreenState =>
  updateSettings(s, id, e => ({ ...e.settings, joinDays }));

export const withIncludeCourse = (s: ImportScreenState, id: string, includeCourse: boolean): ImportScreenState =>
  updateSettings(s, id, e => ({ ...e.settings, includeCourse }));

export const withKeepWaypoints = (s: ImportScreenState, id: string, keepWaypoints: boolean): ImportScreenState =>
  updateSettings(s, id, e => ({ ...e.settings, keepWaypoints }));

export const withIncludeRepeats = (s: ImportScreenState, id: string, includeRepeats: boolean): ImportScreenState =>
  updateSettings(s, id, e => ({ ...e.settings, includeRepeats }));

export const withSnapToPaths = (s: ImportScreenState, id: string, snapToPaths: boolean): ImportScreenState =>
  updateSettings(s, id, e => ({ ...e.settings, snapToPaths }));

/** "Snap all to paths": ticks (or unticks) it on every file it applies to. */
export function withSnapAll(s: ImportScreenState, snapToPaths: boolean): ImportScreenState {
  const ids = new Set(snapAllEntries(s).map(e => e.id));
  return { ...s, entries: s.entries.map(e => (ids.has(e.id) ? { ...e, settings: { ...e.settings, snapToPaths } } : e)) };
}

/** A queued match that's no longer wanted (unticked, or no longer a planned hike) is forgotten, so ticking again redoes it. */
export function withSnapCleared(s: ImportScreenState, id: string, key: string): ImportScreenState {
  if (s.snaps?.[id]?.key !== key) return s;
  const { [id]: _dropped, ...rest } = s.snaps ?? {};
  return { ...s, snaps: rest };
}

/** A file's matching has begun for the days `key` names. */
export const withSnapStarted = (s: ImportScreenState, id: string, key: string): ImportScreenState =>
  findEntry(s, id) ? { ...s, snaps: { ...(s.snaps ?? {}), [id]: { key, matching: true, days: [] } } } : s;

/** A file's matches are in; dropped if the file has gone or its days changed since they started. */
export function withSnapFinished(s: ImportScreenState, id: string, key: string, days: DayMatch[]): ImportScreenState {
  if (!findEntry(s, id) || s.snaps?.[id]?.key !== key) return s;
  return { ...s, snaps: { ...s.snaps, [id]: { key, matching: false, days } } };
}
